import axios from 'axios';
// Importações síncronas das traduções locais para evitar flash
import ptBRTranslations from '../translations/spa-translations.pt-BR.json';
import esESTranslations from '../translations/spa-translations.es-ES.json';

// Configurações do GLOTA
const GLOTA_CONFIG = {
  url: process.env.REACT_APP_GLOTA_URL || 'https://glota-gamma-polidigital.svc-us3.zcloud.ws',
  token: process.env.REACT_APP_GLOTA_TOKEN || 'D14A028C2A3A2BC9476102BB288234C415A2B01F828EA62AC5B3E42F',
  serviceName: process.env.REACT_APP_GLOTA_SERVICE_NAME || 'polichat-spa',
  enabled: process.env.REACT_APP_ENABLE_GLOTA === 'true' // Deve ser exatamente "true" como string
};

// Cache de traduções por locale
let translationsCache = {};
let currentLocale = 'pt-BR';
// Promises de carregamento em andamento para evitar requisições duplicadas
let loadingPromises = {};

// Mapa de traduções locais síncronas
const LOCAL_TRANSLATIONS = {
  'pt-BR': ptBRTranslations,
  'pt': ptBRTranslations,
  'es-ES': esESTranslations,
  'es': esESTranslations,
};

class GlotaTranslationService {
  constructor() {
    this.api = axios.create({
      baseURL: GLOTA_CONFIG.url,
      headers: {
        'Authorization': `Bearer ${GLOTA_CONFIG.token}`,
        'Content-Type': 'application/json',
      },
    });
    
    // Pré-carrega traduções locais no cache para acesso imediato
    this._preloadLocalTranslations();
  }

  /**
   * Pré-carrega traduções locais no cache para acesso imediato
   */
  _preloadLocalTranslations() {
    Object.keys(LOCAL_TRANSLATIONS).forEach(locale => {
      const normalizedLocale = locale === 'pt' ? 'pt-BR' : locale === 'es' ? 'es-ES' : locale;
      if (!translationsCache[normalizedLocale]) {
        translationsCache[normalizedLocale] = LOCAL_TRANSLATIONS[locale];
      }
    });
  }

  /**
   * Obtém traduções locais síncronas (sem esperar)
   */
  getLocalTranslationsSync(locale = null) {
    const targetLocale = locale || currentLocale;
    const normalizedLocale = targetLocale === 'pt' ? 'pt-BR' : targetLocale === 'es' ? 'es-ES' : targetLocale;
    
    return LOCAL_TRANSLATIONS[targetLocale] || LOCAL_TRANSLATIONS[normalizedLocale] || LOCAL_TRANSLATIONS['pt-BR'] || {};
  }

  /**
   * Traduz uma chave usando traduções locais síncronas (sem esperar)
   */
  translateSync(key, params = {}, locale = null) {
    try {
      const translations = this.getLocalTranslationsSync(locale);

      // Navega pela estrutura aninhada da chave
      const keys = key.split('.');
      let current = translations;

      for (const k of keys) {
        if (current && typeof current === 'object' && current[k] !== undefined) {
          current = current[k];
        } else {
          // Se não encontrar tradução, retorna a chave como fallback
          return key;
        }
      }

      let translatedText = typeof current === 'string' ? current : key;

      // Substitui parâmetros se houver
      if (params && Object.keys(params).length > 0) {
        for (const [param, value] of Object.entries(params)) {
          translatedText = translatedText.replace(`{{${param}}}`, String(value));
        }
      }

      return translatedText;
    } catch (error) {
      console.warn('Erro ao traduzir chave (sync):', key, error);
      return key;
    }
  }

  /**
   * Define o locale atual
   */
  setLocale(locale) {
    currentLocale = locale;
    // Limpa cache e Promises em andamento quando muda o locale
    // Mas mantém as traduções locais pré-carregadas
    loadingPromises = {};
    // Recarrega traduções locais se necessário
    if (!translationsCache[locale]) {
      const normalizedLocale = locale === 'pt' ? 'pt-BR' : locale === 'es' ? 'es-ES' : locale;
      if (LOCAL_TRANSLATIONS[locale] || LOCAL_TRANSLATIONS[normalizedLocale]) {
        translationsCache[normalizedLocale] = LOCAL_TRANSLATIONS[locale] || LOCAL_TRANSLATIONS[normalizedLocale];
      }
    }
  }

  /**
   * Obtém o locale atual
   */
  getCurrentLocale() {
    return currentLocale;
  }

  /**
   * Mescla traduções locais com traduções do GLOTA
   */
  mergeTranslations(localTranslations, glotaTranslations) {
    // Deep merge das traduções
    const merged = { ...localTranslations };

    // Sobrescreve com traduções do GLOTA se disponíveis
    for (const [key, value] of Object.entries(glotaTranslations)) {
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if (merged[key] && typeof merged[key] === 'object') {
          merged[key] = this.mergeTranslations(merged[key], value);
        } else {
          merged[key] = value;
        }
      } else {
        merged[key] = value;
      }
    }

    return merged;
  }

  /**
   * Obtém traduções com prioridade para arquivo local e fallback para GLOTA
   */
  async getTranslations(locale = null) {
    const targetLocale = locale || currentLocale;
    const normalizedLocale = targetLocale === 'pt' ? 'pt-BR' : targetLocale === 'es' ? 'es-ES' : targetLocale;

    // Retorna traduções locais imediatamente se já estiverem em cache
    if (translationsCache[normalizedLocale]) {
      return translationsCache[normalizedLocale];
    }

    // Se não estiver em cache mas temos traduções locais, usa elas imediatamente
    const localTranslations = this.getLocalTranslationsSync(targetLocale);
    if (localTranslations && Object.keys(localTranslations).length > 0) {
      translationsCache[normalizedLocale] = localTranslations;
    }

    // Se o Glota não está habilitado, retorna apenas traduções locais
    if (!GLOTA_CONFIG.enabled) {
      return Promise.resolve(localTranslations);
    }

    // Verifica se já há uma requisição em andamento para este locale
    // Retorna a mesma Promise para evitar requisições duplicadas
    if (loadingPromises[normalizedLocale]) {
      return loadingPromises[normalizedLocale];
    }

    // Cria nova Promise de carregamento para buscar do GLOTA em background
    const loadPromise = this._loadTranslationsFromGlota(normalizedLocale);
    loadingPromises[normalizedLocale] = loadPromise;

    // Remove da lista de Promises em andamento após completar
    loadPromise.finally(() => {
      delete loadingPromises[normalizedLocale];
    });

    // Retorna traduções locais imediatamente enquanto busca do GLOTA em background
    return Promise.resolve(localTranslations).then(() => {
      return loadPromise;
    });
  }

  async _loadTranslationsFromGlota(targetLocale) {
    // Já temos traduções locais, vamos buscar do GLOTA para atualizar
    const localTranslations = this.getLocalTranslationsSync(targetLocale);

    // Se o Glota não está habilitado, retorna apenas traduções locais
    if (!GLOTA_CONFIG.enabled) {
      if (!translationsCache[targetLocale]) {
        translationsCache[targetLocale] = localTranslations;
      }
      return localTranslations;
    }

    try {
      // Tenta obter do GLOTA para sobrescrever/complementar
      const response = await this.api.get(`/api/v1/translations/${GLOTA_CONFIG.serviceName}/${targetLocale}`);

      if (response.data && response.data.translations) {
        // Mescla traduções do GLOTA com traduções locais
        const glotaTranslations = response.data.translations;
        const mergedTranslations = this.mergeTranslations(localTranslations, glotaTranslations);
        translationsCache[targetLocale] = mergedTranslations;
        return mergedTranslations;
      }

      // Se não conseguir do GLOTA, usa apenas traduções locais
      if (!translationsCache[targetLocale]) {
        translationsCache[targetLocale] = localTranslations;
      }
      return localTranslations;

    } catch (error) {
      console.warn('Erro ao obter traduções do GLOTA, usando traduções locais:', error);
      // Usa traduções locais como fallback
      if (!translationsCache[targetLocale]) {
        translationsCache[targetLocale] = localTranslations;
      }
      return localTranslations;
    }
  }

  /**
   * Traduz uma chave específica usando o sistema GLOTA
   * Primeiro tenta tradução síncrona local, depois busca do GLOTA se necessário
   */
  async translate(key, params = {}, locale = null) {
    // Primeiro tenta tradução síncrona local (sem esperar)
    const syncTranslation = this.translateSync(key, params, locale);
    
    // Se encontrou tradução local e não é apenas a chave, retorna imediatamente
    if (syncTranslation !== key) {
      // Busca do GLOTA em background para atualizar se necessário
      this.getTranslations(locale).then(() => {
        // Atualiza cache para próxima vez
      }).catch(() => {
        // Ignora erros silenciosamente
      });
      
      return syncTranslation;
    }

    // Se não encontrou localmente, aguarda carregar traduções completas
    try {
      const translations = await this.getTranslations(locale);

      // Navega pela estrutura aninhada da chave
      const keys = key.split('.');
      let current = translations;

      for (const k of keys) {
        if (current && typeof current === 'object' && current[k] !== undefined) {
          current = current[k];
        } else {
          // Se não encontrar tradução, retorna a chave como fallback
          return key;
        }
      }

      let translatedText = typeof current === 'string' ? current : key;

      // Substitui parâmetros se houver
      if (params && Object.keys(params).length > 0) {
        for (const [param, value] of Object.entries(params)) {
          translatedText = translatedText.replace(`{{${param}}}`, String(value));
        }
      }

      return translatedText;
    } catch (error) {
      console.warn('Erro ao traduzir chave:', key, error);
      return key;
    }
  }

  /**
   * Traduz texto diretamente usando o GLOTA
   */
  async translateText(text, sourceLang = 'pt', targetLang = null) {
    // Se o Glota não está habilitado, retorna o texto original
    if (!GLOTA_CONFIG.enabled) {
      return text;
    }

    try {
      const response = await this.api.post('/api/v1/translate', {
        text,
        source_lang: sourceLang,
        target_lang: targetLang || currentLocale,
      });

      if (response.data && response.data.translated_text) {
        return response.data.translated_text;
      }

      return text;
    } catch (error) {
      console.warn('Erro na tradução direta:', error);
      return text;
    }
  }

  /**
   * Obtém lista de idiomas suportados
   */
  async getSupportedLanguages() {
    // Se o Glota não está habilitado, retorna objeto vazio
    if (!GLOTA_CONFIG.enabled) {
      return {};
    }

    try {
      const response = await this.api.get('/api/v1/supported-languages');
      return response.data || {};
    } catch (error) {
      console.warn('Erro ao obter idiomas suportados:', error);
      return {};
    }
  }

  /**
   * Limpa cache de traduções
   */
  clearCache(locale = null) {
    if (locale) {
      delete translationsCache[locale];
      delete loadingPromises[locale];
      // Recarrega traduções locais se necessário
      const normalizedLocale = locale === 'pt' ? 'pt-BR' : locale === 'es' ? 'es-ES' : locale;
      if (LOCAL_TRANSLATIONS[locale] || LOCAL_TRANSLATIONS[normalizedLocale]) {
        translationsCache[normalizedLocale] = LOCAL_TRANSLATIONS[locale] || LOCAL_TRANSLATIONS[normalizedLocale];
      }
    } else {
      translationsCache = {};
      loadingPromises = {};
      // Recarrega traduções locais
      this._preloadLocalTranslations();
    }
  }

  /**
   * Carrega traduções para um locale específico
   */
  async loadTranslationsForLocale(locale) {
    return await this.getTranslations(locale);
  }
}

// Exporta instância singleton
const glotaTranslationService = new GlotaTranslationService();

export { glotaTranslationService };
export default glotaTranslationService;
